import React, { useCallback, useState, useMemo } from "react";
import {
    Link,
    useLocation,
    useNavigate,
    useSearchParams,
} from "react-router-dom";

import { FaEye } from "react-icons/fa";
import { MdDelete } from "react-icons/md";
import { MdEdit } from "react-icons/md";

import {
    convertURLParams,
    formatDateTime,
    parseSearchParams,
    formatCurrency,
} from "@/utils/helpers";

import Modal from "@/components/Modal";
import ConfirmDelete from "@/components/ConfirmDelete";
import Table from "../../components/Table/Table";
import HeaderContent from "../../components/Table/components/HeaderContent";
import FooterContent from "../../components/Table/components/FooterContent";

import ViewOffer from "./ViewOffer";

import usePermission from "../../../../hooks/usePermission";
import { useOffers } from "../hooks/useOffers";
import { useDeleteOffer } from "../hooks/useDeleteOffer";
import { BiSolidCoupon } from "react-icons/bi";
import { schoolLevelIds, translatedSchoolGrades, translatedSchoolLevels } from "../../../../utils/constants";

function OffersTable() {
    const { search } = useLocation();
    const [searchValue, setSearchValue] = useState("");

    const navigate = useNavigate();
    const [perPage, setPerPage] = useState(10);
    const [searchParams] = useSearchParams();

    const { canAll, can } = usePermission();

    const includeFields = "lectures";
    const include = { field: "Discount", value: true };

    const page = parseSearchParams(
        searchParams,
        "page",
        (value) => (parseInt(value, 10) < 1 ? 1 : parseInt(value, 10)),
        1
    );

    const filterAndSortAndPageQuery = convertURLParams(search);

    //Hooks

    //Get Offers
    const { offers, isPending } = useOffers({
        includeFields,
        include,
        page,
        perPage,
        searchValue,
        filterAndSortAndPageQuery,
    });

    //Delete Offer
    const { deleteOffer, isDeleting } = useDeleteOffer();

    //Filter Options
    const filterOptions = [
        { name: "طلاب الصف الأول", field: "grade", value: "1" },
        { name: "طلاب الصف الثاني", field: "grade", value: "2" },
        { name: "طلاب الصف الثالث", field: "grade", value: "3" },
    ];

    //Header Rows
    const headers = [
        { uid: "id", name: "#", sortable: true },
        { uid: "name", name: "الاسم", sortable: true },
        { uid: "grade", name: "الصف", sortable: false },
        { uid: "price", name: "السعر", sortable: true },
        { uid: "discount", name: "الخصم", sortable: true },
        { uid: "createdAt", name: "وقت الإنشاء", sortable: true },
        { uid: "updatedAt", name: "أخر تعديل", sortable: true },
        { uid: "actions", name: "الافعال", sortable: false },
    ];

    //Default Headers
    const INITIAL_VISIBLE_COLUMNS = [
        "id",
        "name",
        "grade",
        "price",
        "discount",
        "createdAt",
        "actions",
    ];

    //Actions
    const actions = useMemo(
        () => [
            {
                id: "rowDetails",
                name: "رؤية",
                icon: <FaEye className="text-lg" />,
                content: (row) => <ViewOffer offer={row} />,
                permissions: ["read:offers"],
            },
            {
                id: "edit",
                icon: <MdEdit className="text-lg text-blue-color-primary" />,
                permissions: ["update:offers"],
                to: (id) => `/admin/offers/${id}/edit`,
            },
            {
                id: "delete",
                name: "حذف",
                icon: <MdDelete className="text-lg text-red-color-primary" />,
                permissions: ["delete:offers"],
            },
        ],
        []
    );

    //Add Row
    const addRow = {
        to: "/admin/offers/create",
        permission: can("create:offers"),
    };
    //formatting Data
    const reformattedData = offers?.data?.map((item) => {
        const {
            id,
            attributes,
            relationships: { lectures },
            discount,
        } = item;

        return {
            id,
            ...attributes,
            discount,
            lectures,
        };
    });

    const renderCell = useCallback(
        (row, columnKey) => {
            const cellValue = row?.[columnKey];
            switch (columnKey) {
                case "grade": {
                    const [key, value] = Object.entries(schoolLevelIds).find(
                        ([, value]) => value === row.school_level
                    ) || [];
                    return key ? `${translatedSchoolGrades[cellValue]} ${translatedSchoolLevels[value]}` : null;
                }
                case "price":
                    return cellValue !== 0 ? (
                        <span className="text-green-700">
                            {formatCurrency(cellValue)}
                        </span>
                    ) : (
                        <span className="text-green-700">ببلاش</span>
                    );
                case "discount":
                    return cellValue === null ? (
                        <span>{formatCurrency(1)}</span>
                    ) : cellValue >= 0 ? (
                        <span className="text-green-700">
                            {formatCurrency(cellValue)}
                        </span>
                    ) : (
                        <span className="text-red-700">
                            {formatCurrency(-1 * cellValue)}
                        </span>
                    );
                case "createdAt":
                    return (
                        <span className="flex flex-col gap-2">
                            <span>
                                {formatDateTime(cellValue).formattedTime}
                            </span>
                            <span>
                                {formatDateTime(cellValue).formattedDate}
                            </span>
                        </span>
                    );
                case "updatedAt":
                    return (
                        <span className="flex flex-col gap-2">
                            <span>
                                {formatDateTime(cellValue).formattedTime}
                            </span>
                            <span>
                                {formatDateTime(cellValue).formattedDate}
                            </span>
                        </span>
                    );
                case "actions":
                    return (
                        <div className="relative flex items-center justify-start gap-2">
                            {can("create:offer:vouchers") && (
                                <Link to={`/admin/offers/${row.id}/vouchers`}>
                                    <BiSolidCoupon className="text-lg text-blue-color-primary" />
                                </Link>
                            )}
                            <Modal key={row.id}>
                                {actions?.map(
                                    (action) =>
                                        canAll(action.permissions) && (
                                            <>
                                                {action.id === "edit" ? (
                                                    <button
                                                        onClick={() =>
                                                            navigate(
                                                                action.to(
                                                                    row.id
                                                                ),
                                                                { state: row }
                                                            )
                                                        }
                                                        key={action.id}
                                                    >
                                                        {action.icon}
                                                    </button>
                                                ) : (
                                                    <React.Fragment
                                                        key={action.id}
                                                    >
                                                        <Modal.Open
                                                            opens={action.id}
                                                        >
                                                            <button>
                                                                {action.icon}
                                                            </button>
                                                        </Modal.Open>

                                                        <Modal.Window
                                                            name={action.id}
                                                        >
                                                            {action.id ===
                                                                "rowDetails" ? (
                                                                action.content(
                                                                    row
                                                                )
                                                            ) : action.id ===
                                                                "delete" ? (
                                                                <ConfirmDelete
                                                                    rowData={
                                                                        row
                                                                    }
                                                                    onConfirm={() =>
                                                                        deleteOffer(
                                                                            row?.id
                                                                        )
                                                                    }
                                                                    disabled={
                                                                        isDeleting
                                                                    }
                                                                    resourceName={
                                                                        row?.name
                                                                    }
                                                                />
                                                            ) : null}
                                                        </Modal.Window>
                                                    </React.Fragment>
                                                )}
                                            </>
                                        )
                                )}
                            </Modal>
                        </div>
                    );
                default:
                    return cellValue;
            }
        },
        [actions, isDeleting, canAll, can, navigate, deleteOffer]
    );

    const [visibleColumns, setVisibleColumns] = useState(
        new Set(INITIAL_VISIBLE_COLUMNS)
    );

    const totalPages = Math.ceil(offers?.meta?.total / offers?.meta?.per_page);

    return (
        <Table
            isloading={isPending}
            rows={reformattedData}
            headers={headers}
            visibleColumns={visibleColumns}
            renderCell={renderCell}
            columnsToCopy={["name", "grade"]}
            topContent={
                <HeaderContent
                    filterOptions={filterOptions}
                    visibleColumns={visibleColumns}
                    setVisibleColumns={setVisibleColumns}
                    headers={headers}
                    rowsNumber={perPage}
                    setRowsNumber={setPerPage}
                    addRow={addRow}
                    totalRows={offers?.meta?.total}
                    searchValue={searchValue}
                    setSearchValue={setSearchValue}
                    placeholder={"يمكنك البحث بالاسم او الوصف او ال ID"}
                />
            }
            bottomContent={<FooterContent totalPages={totalPages} />}
        />
    );
}

export default OffersTable;
